<template>
  <table-wrapper>
    <div class="coronet-table--loader">
      <v-progress-linear
        v-if="loading"
        data-testid="table-progress-bar"
        indeterminate
        height="2px"
      ></v-progress-linear>
    </div>
    <v-data-table-server
      class="coronet-table--highlightable"
      :fixed-header="true"
      :items="items"
      :headers="headers"
      :items-per-page="pagination.pageSize"
      :items-length="totalItems"
      item-value="userId"
      show-select
      return-object
      density="compact"
      v-model="selectedItems"
      v-model:expanded="expanded"
      @update:options="
        $emit('page-changed', {
          pageSize: $event.itemsPerPage,
          page: $event.page - 1,
        })
      "
    >
      <template #headers="{ columns }">
        <tr>
          <th
            v-for="header of columns"
            :key="header.key as string"
            class="checkbox-col"
            :class="getHeaderColumnStyles(header)"
            :aria-label="
              header.value === 'data-table-select'
                ? $t('ariaLabels.checkboxColumn')
                : $t(`usersSettings.usersTab.tableHeaders.${header.key}`)
            "
          >
            <bulk-selector-checkbox
              v-if="header.value === 'data-table-select'"
              class="mx-auto"
              :is-indeterminate="isHeaderIndeterminate"
              :all-items-on-page-selected="allItemsOnPageSelected"
              :items="items"
              :showMenu="false"
              :all-items-selected="isHeaderChecked"
              @selection-changed="onSelectionChanged"
            />
            <template v-else-if="header.key === 'user' && selectedItems.length > 0">
              <span>{{ $t("usersSettings.usersTab.usersSelected", selectedItems.length) }}</span>
              <v-menu bottom left>
                <template #activator="{ props: menuProps, isActive }">
                  <v-btn rounded color="primary" class="ml-2" v-bind="menuProps">
                    {{ $t("usersSettings.usersTab.actionsBtn") }}
                    <v-icon
                      class="ml-2"
                      size="24"
                      icon="$triangle"
                      :class="{ rotated: isActive }"
                    />
                  </v-btn>
                </template>
                <v-list>
                  <template v-for="action in bulkActions" :key="action">
                    <v-list-item @click="handleItemAction(action, { isBulk: true })">
                      <v-list-item-title>
                        {{ $t(`usersSettings.usersTab.userActions.${action}`, 1) }}
                      </v-list-item-title>
                    </v-list-item>
                  </template>
                </v-list>
              </v-menu>
            </template>
            <span v-else :data-testid="`table-${header.key}-header`">
              {{ $t(`usersSettings.usersTab.tableHeaders.${header.key}`) }}
            </span>
          </th>
          <th
            class="checkbox-col text-center pa-0 coro-table-col--sticky-right"
            :aria-label="$t('ariaLabels.actionsColumn')"
          ></th>
        </tr>
      </template>
      <!--Slot for rows-->
      <template #item="{ item, index, internalItem, isSelected }">
        <tr
          :key="item.userId"
          :id="`${index}`"
          :data-testid="`table-row-${index}`"
          :class="[
            'service-row',
            { 'service-row--tall': hasInactiveApp(item) || item.aliases?.length > 0 },
          ]"
        >
          <!-- Checkbox -->
          <td class="pa-0 text-center checkbox-col" @click="$event.stopPropagation()">
            <v-checkbox-btn
              :model-value="isSelected([internalItem])"
              :aria-label="$t('ariaLabels.checkboxColumn')"
              :data-testid="`table-select-checkbox-row-${index}`"
              @update:modelValue="handleOneRowSelection($event, item)"
              @click.stop
            ></v-checkbox-btn>
          </td>

          <!-- User email -->
          <td class="pl-0 user-email-col" :data-testid="`users-table-user-col-row-${index}`">
            <div>
              <v-tooltip open-delay="300" location="top">
                <template #activator="{ props: tooltipProps }">
                  <b
                    class="d-block ellipsis"
                    v-bind="tooltipProps"
                    :class="{
                      strikethrough: getIsExcepted(item),
                    }"
                    >{{ item.email }}</b
                  >
                </template>
                <span :data-testid="`users-table-user-email-tooltip-${index}`">{{
                  item.email
                }}</span>
              </v-tooltip>
              <span v-if="getIsExcepted(item)">
                {{ $t(`usersSettings.usersTab.excluded`) }}
              </span>
            </div>
            <email-aliases
              class="users-settings-table-aliases"
              :aliases="item.aliases"
              :show-tooltip="true"
            />
          </td>
          <!-- Protection status -->
          <td class="protection-status-col">
            <div class="d-flex align-center">
              <span v-if="item.isProtected" class="text-green-dark">{{
                $t("usersSettings.usersTab.protected")
              }}</span>
              <span v-else class="text-red-dark">{{
                $t("usersSettings.usersTab.unprotected")
              }}</span>
              <v-tooltip v-if="item.manualDecision" open-delay="300" location="top">
                <template #activator="{ props: tooltipProps }">
                  <v-icon
                    class="ml-1"
                    v-bind="tooltipProps"
                    icon="icon-info-solid-xs"
                    size="16"
                    :data-testid="`users-table-manual-decision-icon-row-${index}`"
                  />
                </template>
                <span :data-testid="`users-table-manual-decision-tooltip-row-${index}`">
                  {{ $t(`usersSettings.usersTab.manualDecisionTooltips.${item.manualDecision}`) }}
                </span>
              </v-tooltip>
            </div>
            <div v-if="hasInactiveApp(item)" class="text-indigo-medium caption">
              {{ $t("usersSettings.usersTab.inactive") }}
            </div>
          </td>
          <!-- Protected via -->
          <td class="protected-via-col" :data-testid="`users-table-joined-via-col-row-${index}`">
            <!-- Joined via INDIVIDUALLY, CSV, ADMIN -->
            {{ getLabelForMultipleJoinedVia(item) }}
            <!-- Joined via GROUP -->
            <div
              class="d-flex align-center"
              v-if="item.protectedVia.includes(UserSettingsJoinedVia.GROUP)"
            >
              <coro-icon
                class="service-icon"
                :class="{
                  'service-icon-excepted': item.exceptedUser,
                }"
                :icon-name="item.groups[0]?.service"
              ></coro-icon>
              <span class="ml-1 mr-2">
                {{ item.groups[0]?.name }}
                <v-tooltip v-if="item.groups.length > 1" open-delay="300" location="top">
                  <template #activator="{ props }">
                    <span class="ml-1" v-bind="props">
                      {{ `+${item.groups.length - 1}` }}
                    </span>
                  </template>
                  <div>
                    <div
                      v-for="group of item.groups"
                      :key="group.name"
                      class="d-flex align-center text-primary"
                    >
                      <span class="text-white">{{ group.name }}</span>
                    </div>
                  </div>
                </v-tooltip>
              </span>
            </div>
            <!-- Joined via INBOUND_GATEWAY -->
            <div v-if="item.protectedVia.includes(UserSettingsJoinedVia.INBOUND_GATEWAY)">
              <v-icon
                size="30"
                icon="$globe"
                :class="{
                  'service-icon-excepted': item.exceptedUser,
                }"
              />
              <span class="ml-1 mr-2">
                {{ getDomainFromEmail(item.email) }}
              </span>
            </div>
            <!-- Services from protectedVia -->
            <div class="d-flex align-center" v-if="getServicesFromProtectedVia(item).length">
              <template
                v-for="service in getServicesFromProtectedVia(item).slice(
                  0,
                  MAX_PROTECTED_VIA_SERVICES_SHOWN
                )"
                :key="service"
              >
                <coro-icon class="service-icon" :icon-name="service"></coro-icon>
                <span :data-testid="`users-table-protected-via-service-${index}`">
                  {{ $t("usersSettings.usersTab.cloudSync") }}
                </span>
              </template>
              <v-tooltip
                v-if="getServicesFromProtectedVia(item).length > MAX_PROTECTED_VIA_SERVICES_SHOWN"
                open-delay="300"
                location="top"
              >
                <template #activator="{ props }">
                  <span class="ml-1" v-bind="props">
                    {{
                      `+${getServicesFromProtectedVia(item).length - MAX_PROTECTED_VIA_SERVICES_SHOWN}`
                    }}
                  </span>
                </template>
                <div :data-testid="`users-table-protected-via-services-tooltip-${index}`">
                  {{ getServicesFromProtectedViaTooltipData(getServicesFromProtectedVia(item)) }}
                </div>
              </v-tooltip>
            </div>
          </td>
          <!-- Connected apps -->
          <td class="connected-apps-col" :data-testid="`users-table-protected-by-col-row-${index}`">
            <div class="d-flex align-center" v-if="item.connectedCloudApplications">
              <template
                v-for="protectionService in item.connectedCloudApplications.slice(
                  0,
                  MAX_ICONS_SHOWN
                )"
                :key="protectionService.appName"
              >
                <coro-icon
                  class="service-icon"
                  :class="{ 'service-icon-inactive': !protectionService.active }"
                  :icon-name="protectionService.appName"
                ></coro-icon>
              </template>
              <v-tooltip open-delay="300" location="top">
                <template #activator="{ props }">
                  <span
                    v-if="item.connectedCloudApplications.length > MAX_ICONS_SHOWN"
                    v-bind="props"
                  >
                    {{ `+${item.connectedCloudApplications.length - MAX_ICONS_SHOWN}` }}
                  </span>
                </template>
                <div>
                  {{ getConnectedAppsTooltipData(item.connectedCloudApplications) }}
                </div>
              </v-tooltip>
            </div>
          </td>
          <!-- Groups -->
          <td>
            <div class="d-flex align-center" v-if="item.groups?.length">
              <span>{{ item.groups[0]?.name }}</span>
              <v-tooltip v-if="item.groups.length > 1" open-delay="300" location="top">
                <template #activator="{ props }">
                  <span class="ml-1" v-bind="props">
                    {{ `+${item.groups.length - 1}` }}
                  </span>
                </template>
                <div>
                  <div class="text-indigo-light">{{ $t("usersSettings.usersTab.groups") }}</div>
                  <div v-for="group of item.groups.slice(1)" :key="group.name">
                    {{ group.name }}
                  </div>
                  <div
                    v-if="item.protectedVia.includes(UserSettingsJoinedVia.INDIVIDUALLY)"
                    class="mt-2"
                  >
                    <div class="text-indigo-light">{{ $t("usersSettings.usersTab.other") }}</div>
                    <div>{{ $t("usersSettings.usersTab.directMember") }}</div>
                  </div>
                </div>
              </v-tooltip>
            </div>
          </td>
          <!-- Joined since -->
          <td
            class="text-right pr-0"
            :data-testid="`users-table-protection-granted-col-row-${index}`"
          >
            {{ getFormattedDateTime(item.joinedSince, "ddd, MMM D YYYY, hh:mm A") }}
          </td>
          <!-- Actions -->
          <td
            @click="$event.stopPropagation()"
            class="pa-0 checkbox-col coro-table-col--sticky-right"
          >
            <div class="d-flex justify-center">
              <v-menu v-if="showAllActions(item)" bottom left>
                <template #activator="{ props }">
                  <!--hiding toggler for non-protectable users-->
                  <v-btn
                    dark
                    icon
                    :aria-label="$t('ariaLabels.openMenuButton')"
                    :data-testid="`users-table-action-btn-row-${index}`"
                    v-bind="props"
                  >
                    <v-icon icon="$dots" />
                  </v-btn>
                </template>

                <v-list>
                  <template v-for="userAction in getActions(item)" :key="userAction">
                    <v-list-item @click="handleItemAction(userAction, { item, isBulk: false })">
                      <v-list-item-title :data-testid="`users-table-${userAction}-action`">
                        {{ $t(`usersSettings.usersTab.userActions.${userAction}`, 1) }}
                      </v-list-item-title>
                    </v-list-item>
                  </template>
                </v-list>
              </v-menu>
            </div>
          </td>
        </tr>
      </template>
      <template #no-data>
        <v-icon class="mt-3 mb-3" icon="$users" size="80" />
        <div class="headline5 mb-3">
          {{ $t("usersSettings.usersTab.noUsers") }}
        </div>
        <a
          data-testid="users-table-add-users-btn"
          class="coro-link body1"
          @click="handleItemAction(userActions.ADD_USERS, { isBulk: false })"
          >{{ $t("usersSettings.addUsersBtn") }}</a
        >
      </template>
    </v-data-table-server>
  </table-wrapper>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType, ref, toRefs } from "vue";
import CoroIcon from "@/components/CoroIcon.vue";
import TableWrapper from "@/components/TableWrapper.vue";
import EmailAliases from "@/components/EmailAliases.vue";
import BulkSelectorCheckbox from "@/components/BulkSelectorCheckbox.vue";

import { getDomainFromEmail } from "@coronet/cui";
import { getFormattedDateTime } from "@/_helpers/date";
import { UserAction } from "@/constants/users";
import { CoroIcons } from "@/constants/coro-icon";
import {
  UserSettingsJoinedVia,
  type UserSettingsListItem,
  type UserSettingsProtectedApp,
  type AddUsersPayload,
  useUsersSettingsTabStore,
} from "@/_store/users-settings/users-tab.module";
import type { Pagination } from "@/types";
import { useI18n } from "vue-i18n";
import { Service } from "@/constants/cloud-apps.ts";
import { useSelectableTable } from "@/composables/useSelectableTable.ts";
import { type DialogConfig, useDialogsStore } from "@/_store/dialogs.module.ts";
import AddUsersModal from "@/components/modals/AddUsersModal.vue";
import { ModalWidth } from "@/constants/modals.ts";
import cloneDeep from "lodash/cloneDeep";
import ConfirmationWithCheckboxModal from "@/components/modals/ConfirmationWithCheckboxModal.vue";
import { useWorkspaceStore } from "@/_store/workspace.module.ts";
import { storeToRefs } from "pinia";
import type { InternalDataTableHeader } from "vuetify/lib/components/VDataTable/types.js";
import { isWorkspaceFrozenOrActionRestricted } from "@/_helpers/permissions.ts";
import {
  componentDialogsConfigConstructor,
  confirmationDialogsConfigConstructor,
} from "@/_helpers/dialog-helpers.ts";
import { RolePermissionsScope, WorkspaceManagementScopeSections } from "@/constants/roles.ts";

export default defineComponent({
  components: {
    CoroIcon,
    TableWrapper,
    EmailAliases,
    BulkSelectorCheckbox,
  },
  props: {
    items: {
      type: Array as PropType<UserSettingsListItem[]>,
      required: true,
    },
    totalItems: {
      type: Number,
      required: true,
    },
    pagination: {
      type: Object as PropType<Pagination>,
      default: () => {
        return {
          page: 0,
          pageSize: 15,
        };
      },
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["item-action-applied", "page-changed"],
  setup(props, { emit }) {
    const MAX_ICONS_SHOWN = 3;
    const MAX_PROTECTED_VIA_SERVICES_SHOWN = 1;
    const defaultBulkActions: UserAction[] = [
      UserAction.REMOVE_USER_FROM_PROTECTION,
      UserAction.ADD_USER_TO_PROTECTION,
    ];
    const bulkProtectionActions = new Set<UserAction>([
      UserAction.REMOVE_USER_FROM_PROTECTION,
      UserAction.ADD_USER_TO_PROTECTION,
      UserAction.REMOVE_CLOUD_PROTECTION,
      UserAction.ADD_CLOUD_PROTECTION,
    ]);

    const i18n = useI18n();

    const { totalItems, items } = toRefs(props);
    const { toggleUserProtectionStatus, addUsers, removeUsers, getUsersProtectionStatus } =
      useUsersSettingsTabStore();
    const dialogsStore = useDialogsStore();
    const workspaceStore = useWorkspaceStore();
    const { workspace } = storeToRefs(workspaceStore);

    const {
      selectedItems,
      handleOneRowSelection,
      onSelectionChanged,
      isIndeterminate,
      allItemsSelected,
      allItemsOnPageSelected,
    } = useSelectableTable({
      totalItems,
      items,
    });

    const isHeaderChecked = computed(() => allItemsSelected.value || allItemsOnPageSelected.value);

    const isHeaderIndeterminate = computed(
      () => !isHeaderChecked.value && isIndeterminate.value && selectedItems.value.length > 0
    );

    const actionNotAllowed = computed(() => {
      return isWorkspaceFrozenOrActionRestricted(
        RolePermissionsScope.WORKSPACE_MANAGEMENT,
        WorkspaceManagementScopeSections.USERS
      );
    });

    const getActions = (item: UserSettingsListItem) => {
      const actions: UserAction[] = [];
      const isGroup = item.protectedVia.includes(UserSettingsJoinedVia.GROUP);
      if (item.isProtected) {
        actions.push(
          isGroup ? UserAction.REMOVE_CLOUD_PROTECTION : UserAction.REMOVE_USER_FROM_PROTECTION
        );
      } else {
        actions.push(isGroup ? UserAction.ADD_CLOUD_PROTECTION : UserAction.ADD_USER_TO_PROTECTION);
      }

      if (!item.protectedVia.includes(UserSettingsJoinedVia.INDIVIDUALLY)) {
        actions.push(UserAction.ADD_AS_INDIVIDUAL);
      }

      const canBeRemoved =
        item.removable &&
        (item.protectedVia.includes(UserSettingsJoinedVia.INDIVIDUALLY) ||
          item.protectedVia.includes(UserSettingsJoinedVia.CSV));
      if (canBeRemoved) {
        actions.push(UserAction.REMOVE_USERS);
      }

      return actions;
    };

    const getIsExcepted = (item: UserSettingsListItem) => {
      return item.exceptedUser;
    };

    const showAllActions = (item: UserSettingsListItem) => {
      return !!getActions(item)?.length;
    };

    const handleItemAction = (
      action: string,
      additionalData: { item?: UserSettingsListItem; isBulk: boolean }
    ) => {
      const { isBulk } = additionalData;
      const item = isBulk ? selectedItems.value[0] : additionalData.item;

      let dialogConfig = {} as DialogConfig;
      const text =
        isBulk && selectedItems.value.length > 1
          ? i18n.t(`modals.${action}.descriptionBulk`)
          : i18n.t(`modals.${action}.description`, {
              email: item?.email,
            });
      const affectedUsers: UserSettingsListItem[] = isBulk
        ? selectedItems.value
        : item
          ? [item]
          : [];
      switch (action) {
        case UserAction.ADD_AS_INDIVIDUAL: {
          dialogConfig = confirmationDialogsConfigConstructor({
            action: action,
            item: {
              ...cloneDeep(item),
            },
            text,
            width: ModalWidth.SMALL,
            callback: async () => {
              await addUsers({
                users: affectedUsers.map((user) => ({
                  email: user.email,
                  service: Service.CORONET,
                })),
                workspaceId: workspace.value.workspaceId,
              });
              await getUsersProtectionStatus();
            },
            disable: actionNotAllowed.value,
          });

          break;
        }
        case UserAction.ADD_USERS:
          dialogConfig = componentDialogsConfigConstructor({
            action: action,
            component: AddUsersModal,
            width: ModalWidth.LARGE,
            callback: async (data: AddUsersPayload) => {
              await addUsers(data);
              await getUsersProtectionStatus();
            },
            disable: actionNotAllowed.value,
          });
          break;
        case UserAction.ADD_USER_TO_PROTECTION:
          dialogConfig = confirmationDialogsConfigConstructor({
            action: action,
            item: {
              ...cloneDeep(item),
            },
            text,
            width: ModalWidth.SMALL,
            callback: async () => {
              await toggleUserProtectionStatus({ protected: true, users: affectedUsers });
              await getUsersProtectionStatus();
            },
            disable: actionNotAllowed.value,
          });

          break;
        case UserAction.REMOVE_USERS:
          dialogConfig = componentDialogsConfigConstructor({
            item: {
              ...cloneDeep(item),
            },
            action,
            component: ConfirmationWithCheckboxModal,
            width: ModalWidth.SMALL,
            callback: async () => {
              await removeUsers({ users: affectedUsers });
              await getUsersProtectionStatus();
            },
            disable: actionNotAllowed.value,
          });
          dialogConfig.data.item.text = text;
          dialogConfig = {
            ...dialogConfig,
            header: {
              titleClass: "text-red-dark",
              titleIcon: "warning",
              title: i18n.t(`modals.${UserAction.REMOVE_USERS}.title`),
            },
          };
          break;
        case UserAction.REMOVE_USER_FROM_PROTECTION:
          dialogConfig = confirmationDialogsConfigConstructor({
            item: {
              ...cloneDeep(item),
              protected: false,
            },
            text,
            action,
            disable: actionNotAllowed.value,
            callback: async () => {
              await toggleUserProtectionStatus({ protected: false, users: affectedUsers });
              await getUsersProtectionStatus();
            },
          });

          dialogConfig = {
            ...dialogConfig,
            header: {
              titleClass: "text-red-dark",
              titleIcon: "warning",
              titleIconSize: 24,
              title: i18n.t("modals.removeUserFromProtection.title"),
            },
          };
          break;
        case UserAction.ADD_CLOUD_PROTECTION:
          dialogConfig = confirmationDialogsConfigConstructor({
            action: action,
            item: {
              ...cloneDeep(item),
            },
            text,
            width: ModalWidth.SMALL,
            callback: async () => {
              await toggleUserProtectionStatus({ protected: true, users: affectedUsers });
              await getUsersProtectionStatus();
            },
            disable: actionNotAllowed.value,
          });
          break;
        case UserAction.REMOVE_CLOUD_PROTECTION:
          dialogConfig = confirmationDialogsConfigConstructor({
            item: {
              ...cloneDeep(item),
              protected: false,
            },
            text,
            action,
            disable: actionNotAllowed.value,
            callback: async () => {
              await toggleUserProtectionStatus({ protected: false, users: affectedUsers });
              await getUsersProtectionStatus();
            },
          });

          dialogConfig = {
            ...dialogConfig,
            header: {
              titleClass: "text-red-dark",
              titleIcon: "warning",
              title: i18n.t(`modals.${UserAction.REMOVE_CLOUD_PROTECTION}.title`),
            },
          };
          break;
      }
      dialogsStore.openDialog(dialogConfig);
      emit("item-action-applied", {
        action,
        item,
      });
    };

    const expanded = ref<string[]>([]);

    const getConnectedAppsTooltipData = (connectedApps: UserSettingsProtectedApp[]): string => {
      const itemsToShowTooltipFor = connectedApps.slice(MAX_ICONS_SHOWN, connectedApps.length);
      return itemsToShowTooltipFor.map(({ appName }) => i18n.t(`services.${appName}`)).join(", ");
    };

    const joinedViaValues = new Set<string>(Object.values(UserSettingsJoinedVia));

    const getServicesFromProtectedVia = (item: UserSettingsListItem): Service[] => {
      return item.protectedVia.filter((v) => !joinedViaValues.has(v)) as Service[];
    };

    const getServicesFromProtectedViaTooltipData = (services: Service[]): string => {
      return services
        .slice(MAX_PROTECTED_VIA_SERVICES_SHOWN)
        .map((service) => i18n.t(`services.${service}`))
        .join(", ");
    };

    const hasInactiveApp = (item: UserSettingsListItem): boolean => {
      return item.connectedCloudApplications?.some((app) => !app.active) ?? false;
    };

    const getLabelForMultipleJoinedVia = (item: UserSettingsListItem) => {
      const result = [];

      if (item.protectedVia.includes(UserSettingsJoinedVia.CSV)) {
        result.push(i18n.t("usersSettings.usersTab.addedViaCsv"));
      }

      if (item.protectedVia.includes(UserSettingsJoinedVia.INDIVIDUALLY)) {
        result.push(i18n.t("usersSettings.usersTab.directMember"));
      }

      if (item.protectedVia.includes(UserSettingsJoinedVia.ADMIN)) {
        result.push(i18n.t("general.admin"));
      }

      // If we have several comma separated items - ensure only first one is upper case
      return result.map((item, index) => (index > 0 ? item.toLowerCase() : item)).join(", ");
    };

    const getHeaderColumnStyles = (header: InternalDataTableHeader) => ({
      "text-center pa-0": header.value === "data-table-select",
      "pl-0 user-email-col": header.key === "user",
      "text-right pr-0": header.key === "protectionGranted",
      "connected-apps-col": header.key === "connectedApps",
      "protection-status-col": header.key === "protectionStatus",
      "protected-via-col": header.key === "protectedVia",
    });

    const bulkActions = computed(() => {
      if (!selectedItems.value.length) return [];
      const actionsPerItem = selectedItems.value.map((item) => getActions(item));
      const commonActions = actionsPerItem.reduce((common, actions) =>
        common.filter((action) => actions.includes(action))
      );

      return [
        ...defaultBulkActions,
        ...commonActions.filter((action) => !bulkProtectionActions.has(action)),
      ];
    });

    const headers = [
      {
        key: "user",
        sortable: false,
      },
      {
        key: "protectionStatus",
        sortable: false,
      },
      {
        key: "protectedVia",
        sortable: false,
      },
      {
        key: "connectedApps",
        sortable: false,
      },
      {
        key: "groups",
        sortable: false,
      },
    ];

    headers.push({
      key: "protectionGranted",
      sortable: false,
    });

    return {
      MAX_ICONS_SHOWN,
      MAX_PROTECTED_VIA_SERVICES_SHOWN,
      bulkActions,
      getHeaderColumnStyles,
      getConnectedAppsTooltipData,
      getServicesFromProtectedVia,
      getServicesFromProtectedViaTooltipData,
      getLabelForMultipleJoinedVia,
      userActions: UserAction,
      coronetIcons: CoroIcons,
      Service,
      actionNotAllowed,
      getIsExcepted,
      getActions,
      showAllActions,
      handleItemAction,
      getFormattedDateTime,
      headers,
      expanded,
      UserSettingsJoinedVia,
      hasInactiveApp,
      getDomainFromEmail,
      isIndeterminate,
      isHeaderIndeterminate,
      isHeaderChecked,
      allItemsSelected,
      selectedItems,
      handleOneRowSelection,
      onSelectionChanged,
      allItemsOnPageSelected,
    };
  },
});
</script>

<style scoped lang="scss">
.strikethrough {
  text-decoration: line-through;
}
.service-icon-excepted {
  filter: grayscale(1) saturate(0%);
}
.service-icon-inactive {
  opacity: var(--v-medium-emphasis-opacity);
}
.service-row--tall {
  height: 65px;
}
.service-icon {
  height: 24px;
  width: 24px;
}

:deep(.v-selection-control) {
  justify-content: center;
}

.checkbox-col {
  width: 55px;
  min-width: 55px;
  max-width: 55px;
}

.user-email-col {
  width: 250px;
  max-width: 250px;
}

.connected-apps-col,
.protection-status-col {
  min-width: 110px;
}

.protection-status-col {
  min-width: 130px;
}

.protected-via-col {
  min-width: 200px;
}

:deep(.users-settings-table-aliases) {
  max-width: 250px;
}

:deep(.users-settings-table-aliases .aliases-row__text) {
  min-width: 0;
  flex: 1 1 auto;
}

:deep(.users-settings-table-aliases .aliases-row__popover) {
  flex-shrink: 0;
}
</style>
